import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { fork, put, takeEvery } from "redux-saga/effects";

import {
  BaseCompany,
  companySlice,
  EnrichedProfileStatus,
  FavouriteCompanyData,
} from "../share/company/store";

import { backend } from "../utils/http";
import { State } from "../utils/store";

export const SELECTED_CLIENT_LOCAL_STORAGE_KEY = "selectedClient";

interface ClientAndListUUID {
  clientUUID: string;
  listUUID: string;
}

interface RequestStatus {
  loading: boolean;
  message: string;
  success: boolean | null;
}

export enum ListRequestType {
  Export = 1,
}

export interface ListRequest {
  clientUUID: string;
  listUUID: string;
  requestType: ListRequestType;
  comment?: string;
}

export enum ListCompanySource {
  FAVOURITE = "Favourite",
  CAMPAIGN = "Campaign",
  RTS = "Real-Time Search",
  MATCH = "Match",
  MISS = "Miss",
  TABLE_VIEW = "Table view",
}

export interface CreateListData {
  clientUUID: string;
  name: string;
}

export interface UpdateListData extends ClientAndListUUID {
  name: string;
}

export interface DeleteListData {
  listUUID: string;
}

interface BaseCompanyListRequestData extends ClientAndListUUID {
  companyUUID: string;
  idempotencyKey: string;
}

export interface AddCompanyToListData extends BaseCompanyListRequestData {
  companyName: string;
  addedFrom: ListCompanySource;
}

export interface RemoveCompanyFromListData extends BaseCompanyListRequestData {}

export interface List {
  uuid: string;
  name: string;
  slug: string;
  order: 0;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  requests: {
    exportRequested: boolean;
  };
}

export interface ListCompany {
  uuid: string;
  companyName: string;
  companyUuid: string;
  createdAt: Date;
  updatedAt: Date;
  added_by: string | null; // Null only if it's the auto-generated list of favourites
  added_from: string;
  list: string; // List UUID
  company: BaseCompany;
  enrichedStatus: EnrichedProfileStatus;
}

export interface ListsState {
  getClientLists: {
    loading: boolean;
    error: string;
    success: boolean;
  };
  getListCompanies: {
    loading: boolean;
    error: string;
    success: boolean;
  };
  createList: {
    loading: boolean;
    error: string;
    success: boolean;
  };
  updateList: {
    loading: boolean;
    error: string;
    success: boolean;
  };
  deleteList: {
    loading: boolean;
    error: string;
    success: boolean;
    requestedUUID: string;
  };
  updateLists: {
    loading: boolean;
    error: string;
    success: boolean;
  };
  addCompanyToList: {
    loading: boolean;
    error: string;
    success: boolean;
    responseIdempotencyKey: string | null;
  };
  removeCompanyFromList: {
    loading: boolean;
    error: string;
    success: boolean;
    responseIdempotencyKey: string | null;
  };
  toggleCompanyFavourite: {
    loading: boolean;
    error: string;
    success: boolean;
  };
  lists: List[];
  selectedListCompanies: ListCompany[] | null;
  openedListWidgetCompanyLists: string[];
  createListModalVisible: boolean;
  submitListExportRequest: RequestStatus;
}

const initialState = {
  getClientLists: {
    loading: false,
    error: "",
    success: false,
  },
  getListCompanies: {
    loading: false,
    error: "",
    success: false,
  },
  createList: {
    loading: false,
    error: "",
    success: false,
  },
  updateList: {
    loading: false,
    error: "",
    success: false,
  },
  deleteList: {
    loading: false,
    error: "",
    success: false,
    requestedUUID: "",
  },
  updateLists: {
    loading: false,
    error: "",
    success: false,
  },
  addCompanyToList: {
    loading: false,
    error: "",
    success: false,
    responseIdempotencyKey: null,
  },
  removeCompanyFromList: {
    loading: false,
    error: "",
    success: false,
    responseIdempotencyKey: null,
  },
  toggleCompanyFavourite: {
    loading: false,
    error: "",
    success: false,
  },
  lists: [],
  selectedListCompanies: null,
  openedListWidgetCompanyLists: [],
  createListModalVisible: false,
  submitListExportRequest: {
    loading: false,
    success: null,
    message: "",
  },
} as ListsState;

export const listsSlice = createSlice({
  name: "lists",
  initialState: initialState,
  reducers: {
    setInitialState(state) {
      state = { ...initialState };
    },
    setListExportInitialState(state) {
      state.submitListExportRequest = initialState.submitListExportRequest;
    },
    setCreateListModalVisibility(state, action: PayloadAction<boolean>) {
      state.createListModalVisible = action.payload;
    },
    getClientLists(state, action: PayloadAction<string>) {
      state.getClientLists.loading = true;
      state.getClientLists.success = false;
      state.getClientLists.error = "";
    },
    getClientListsSuccess(state, action) {
      state.getClientLists.loading = false;
      state.getClientLists.success = true;
      state.lists = action.payload.data;
    },
    getClientListsFailure(state, action) {
      state.getClientLists.loading = false;
      state.getClientLists.success = false;
      state.getClientLists.error =
        action.payload.message || action.payload?.data?.message;
    },
    getListCompanies(state, action: PayloadAction<ClientAndListUUID>) {
      state.getListCompanies.loading = true;
      state.getListCompanies.success = false;
      state.getListCompanies.error = "";
    },
    getListCompaniesSuccess(state, action) {
      state.getListCompanies.loading = false;
      state.getListCompanies.success = true;
      state.selectedListCompanies = action.payload.data;
    },
    getListCompaniesFailure(state, action) {
      state.getListCompanies.loading = false;
      state.getListCompanies.success = false;
      state.getListCompanies.error =
        action.payload.message || action.payload?.data?.message;
    },
    setInitialCreateListState(state) {
      state.createList.loading = false;
      state.createList.success = false;
      state.createList.error = "";
    },
    createList(state, action: PayloadAction<CreateListData>) {
      state.createList.loading = true;
      state.createList.success = false;
      state.createList.error = "";
    },
    createListSuccess(state, action) {
      state.createList.loading = false;
      state.createList.success = true;
      state.lists = [action.payload.data, ...state.lists];
    },
    createListFailure(state, action) {
      state.createList.loading = false;
      state.createList.success = false;
      state.createList.error =
        action.payload.message || action.payload?.data?.message;
    },
    setInitialUpdateListState(state) {
      state.updateList.loading = false;
      state.updateList.success = false;
      state.updateList.error = "";
    },
    updateList(state, action: PayloadAction<UpdateListData>) {
      state.updateList.loading = true;
      state.updateList.success = false;
      state.updateList.error = "";
    },
    updateListSuccess(state, action) {
      state.updateList.loading = false;
      state.updateList.success = true;

      const position = getListPositionByListUUID(
        state.lists,
        action.payload.data.uuid
      );
      if (position !== -1) {
        state.lists[position] = action.payload.data;
      }
    },
    updateListFailure(state, action) {
      state.updateList.loading = false;
      state.updateList.success = false;
      state.updateList.error =
        action.payload.message || action.payload?.data?.message;
    },
    deleteList(state, action: PayloadAction<DeleteListData>) {
      state.deleteList.loading = true;
      state.deleteList.success = false;
      state.deleteList.error = "";
      state.deleteList.requestedUUID = action.payload.listUUID;
    },
    deleteListSuccess(state, action) {
      state.deleteList.loading = false;
      state.deleteList.success = true;
      state.deleteList.error = "";
      state.lists = state.lists.filter(
        (list: List) => list.uuid !== action.payload.listUUID
      );
    },
    deleteListFailure(state, action) {
      state.deleteList.loading = false;
      state.deleteList.success = false;
      state.deleteList.error =
        action.payload.message || action.payload?.data?.message;
    },
    addCompanyToList(state, action: PayloadAction<AddCompanyToListData>) {
      state.addCompanyToList.loading = true;
      state.addCompanyToList.responseIdempotencyKey = null;
      state.addCompanyToList.success = false;
      state.addCompanyToList.error = "";
    },
    addCompanyToListSuccess(state, action) {
      state.addCompanyToList.loading = false;
      state.addCompanyToList.success = true;
      state.addCompanyToList.error = "";
      state.addCompanyToList.responseIdempotencyKey =
        action.payload.data?.idempotencyKey;
    },
    addCompanyToListFailure(state, action) {
      state.addCompanyToList.loading = false;
      state.addCompanyToList.success = false;
      state.addCompanyToList.error =
        action.payload.message || action.payload?.data?.message;
      state.addCompanyToList.responseIdempotencyKey =
        action.payload.data?.data?.idempotencyKey;
    },
    removeCompanyFromList(
      state,
      action: PayloadAction<RemoveCompanyFromListData>
    ) {
      state.removeCompanyFromList.loading = true;
      state.removeCompanyFromList.responseIdempotencyKey = null;
      state.removeCompanyFromList.success = false;
      state.removeCompanyFromList.error = "";
    },
    removeCompanyFromListSuccess(
      state,
      action: PayloadAction<RemoveCompanyFromListData>
    ) {
      state.removeCompanyFromList.loading = false;
      state.removeCompanyFromList.success = true;
      state.removeCompanyFromList.error = "";
      state.removeCompanyFromList.responseIdempotencyKey =
        action.payload.idempotencyKey;

      if (state.selectedListCompanies) {
        state.selectedListCompanies = state.selectedListCompanies.filter(
          (listCompany) =>
            listCompany.companyUuid !== action.payload.companyUUID
        );
      }
    },
    removeCompanyFromListFailure(state, action) {
      state.removeCompanyFromList.loading = false;
      state.removeCompanyFromList.success = false;
      state.removeCompanyFromList.error =
        action.payload.message || action.payload?.data?.message;
      state.removeCompanyFromList.responseIdempotencyKey =
        action.payload.data?.data?.idempotencyKey;
    },
    toggleCompanyFavourite(state, action: PayloadAction<FavouriteCompanyData>) {
      state.toggleCompanyFavourite.loading = true;
      state.toggleCompanyFavourite.success = false;
      state.toggleCompanyFavourite.error = "";
    },
    toggleCompanyFavouriteSuccess(
      state,
      action: PayloadAction<FavouriteCompanyData>
    ) {
      state.toggleCompanyFavourite.loading = false;
      state.toggleCompanyFavourite.success = true;
      if (state.selectedListCompanies) {
        const position = getSelectedListCompanyPositionByCompanyUUID(
          state.selectedListCompanies,
          action.payload.companyUUID
        );
        if (position !== -1) {
          state.selectedListCompanies[position].company.favourite =
            !state.selectedListCompanies[position].company.favourite;
        }
      }
    },
    toggleCompanyFavouriteFailure(state, action) {
      state.toggleCompanyFavourite.loading = false;
      state.toggleCompanyFavourite.success = false;
      state.toggleCompanyFavourite.error =
        action.payload.message || action.payload?.data?.message;
    },
    submitListExportRequest(state, action: PayloadAction<ListRequest>) {
      state.submitListExportRequest.loading = true;
      state.submitListExportRequest.success = null;
    },
    submitListExportRequestSuccess(state) {
      state.submitListExportRequest.loading = false;
      state.submitListExportRequest.success = true;
      state.submitListExportRequest.message = "";
    },
    submitListExportRequestFailure(state, action) {
      state.submitListExportRequest.loading = false;
      state.submitListExportRequest.success = false;
      state.submitListExportRequest.message = action.payload.data.message;
    },
    setListExport(state, action) {
      let selectedList = state.lists.find(
        (list) => list.uuid === action.payload.listUUID
      );
      if (selectedList) {
        let updatedList = {
          ...selectedList,
          requests: {
            exportRequested: true,
          },
        };
        state.lists = [
          updatedList,
          ...state.lists.filter(
            (list) => list.uuid !== action.payload.listUUID
          ),
        ];
      }
    },
  },
});

const getListPositionByListUUID = (lists: List[], listUUID: string) =>
  lists.findIndex((list: List) => {
    return list.uuid === listUUID;
  });

const getSelectedListCompanyPositionByCompanyUUID = (
  listCompanies: ListCompany[],
  companyUUID: string
) =>
  listCompanies.findIndex((listCompany: ListCompany) => {
    return listCompany.companyUuid === companyUUID;
  });

export const listsOfCompanies = (state: State) => state.lists.lists;
export const openedListWidgetCompanyLists = (state: State) =>
  state.lists.openedListWidgetCompanyLists;

export const selectedListSlugSelector = (slug: string | undefined) =>
  createSelector(listsOfCompanies, (lists) => {
    if (!slug) return;
    return lists?.find((list) => list.slug === slug);
  });

function* getClientListsSaga() {
  yield takeEvery(listsSlice.actions.getClientLists, function* (action) {
    try {
      const response: AxiosResponse<List[]> =
        yield backend.listBackend.getClientLists(action.payload);

      yield put(listsSlice.actions.getClientListsSuccess(response));
    } catch (err) {
      yield put(listsSlice.actions.getClientListsFailure(err));
    }
  });
}

function* getListCompaniesSaga() {
  yield takeEvery(listsSlice.actions.getListCompanies, function* (action) {
    try {
      const { clientUUID, listUUID } = action.payload;
      const response: AxiosResponse<ListCompany[]> =
        yield backend.listBackend.getListCompanies(listUUID);
      const { data } = response;
      if (data.length > 0) {
        yield put(
          companySlice.actions.fetchCompany({
            clientUUID: clientUUID,
            companyUUID: data[0].companyUuid,
          })
        );
      } else {
        yield put(companySlice.actions.setCompany(null));
      }

      yield put(listsSlice.actions.getListCompaniesSuccess(response));
    } catch (err) {
      yield put(listsSlice.actions.getListCompaniesFailure(err));
    }
  });
}

function* createListSaga() {
  yield takeEvery(listsSlice.actions.createList, function* (action) {
    try {
      const response: AxiosResponse<List[]> =
        yield backend.listBackend.createList(action.payload);

      yield put(listsSlice.actions.createListSuccess(response));
    } catch (err) {
      yield put(listsSlice.actions.createListFailure(err));
    }
  });
}

function* updateListSaga() {
  yield takeEvery(listsSlice.actions.updateList, function* (action) {
    try {
      const response: AxiosResponse<List[]> =
        yield backend.listBackend.updateList(action.payload);

      yield put(listsSlice.actions.updateListSuccess(response));
    } catch (err) {
      yield put(listsSlice.actions.updateListFailure(err));
    }
  });
}

function* deleteListSaga() {
  yield takeEvery(listsSlice.actions.deleteList, function* (action) {
    try {
      yield backend.listBackend.deleteList(action.payload);
      yield put(listsSlice.actions.deleteListSuccess(action.payload));
    } catch (err) {
      yield put(listsSlice.actions.deleteListFailure(err));
    }
  });
}

function* addCompanyToListSaga() {
  yield takeEvery(listsSlice.actions.addCompanyToList, function* (action) {
    try {
      const response: AxiosResponse =
        yield backend.listBackend.addCompanyToList(action.payload);

      yield put(listsSlice.actions.addCompanyToListSuccess(response));
    } catch (err) {
      yield put(listsSlice.actions.addCompanyToListFailure(err));
    }
  });
}

function* removeCompanyFromListSaga() {
  yield takeEvery(listsSlice.actions.removeCompanyFromList, function* (action) {
    try {
      yield backend.listBackend.removeCompanyFromList(action.payload);

      yield put(
        listsSlice.actions.removeCompanyFromListSuccess(action.payload)
      );
    } catch (err) {
      yield put(listsSlice.actions.removeCompanyFromListFailure(err));
    }
  });
}

function* toggleCompanyFavouriteSaga() {
  yield takeEvery(
    listsSlice.actions.toggleCompanyFavourite,
    function* (action) {
      try {
        yield backend.companiesBackend.favouriteCompany(action.payload);

        yield put(
          listsSlice.actions.toggleCompanyFavouriteSuccess(action.payload)
        );
        yield put(companySlice.actions.toggleSelectedCompanyFavourite());
      } catch (err) {
        yield put(listsSlice.actions.toggleCompanyFavouriteFailure(err));
      }
    }
  );
}

function* submitListExportRequestSaga() {
  yield takeEvery(
    listsSlice.actions.submitListExportRequest,
    function* (action) {
      try {
        yield backend.listBackend.submitListExportRequest(action.payload);

        yield put(listsSlice.actions.submitListExportRequestSuccess());
        yield put(listsSlice.actions.setListExport(action.payload));
      } catch (err) {
        yield put(listsSlice.actions.submitListExportRequestFailure(err));
      }
    }
  );
}

export function* listsSaga() {
  yield fork(addCompanyToListSaga);
  yield fork(createListSaga);
  yield fork(deleteListSaga);
  yield fork(getClientListsSaga);
  yield fork(getListCompaniesSaga);
  yield fork(removeCompanyFromListSaga);
  yield fork(toggleCompanyFavouriteSaga);
  yield fork(updateListSaga);
  yield fork(submitListExportRequestSaga);
}

export const {
  addCompanyToList,
  createList,
  deleteList,
  getListCompanies,
  getClientLists,
  removeCompanyFromList,
  setInitialCreateListState,
  setInitialState,
  setInitialUpdateListState,
  setCreateListModalVisibility,
  toggleCompanyFavourite,
  updateList,
  setListExportInitialState,
  submitListExportRequest,
} = listsSlice.actions;
